// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import i18n from "@/i18n";
import { trpcQuery } from "@/lib/api/trpc";
import { getLogger } from "@/lib/logger";
import { vaultKeyGate } from "@/auth/vaultKeyGate";
import { native } from "./index";

export type LocalReminderCategory =
  | "rotation"
  | "review_weak"
  | "review_reused"
  | "review_breached"
  | "backup_unverified"
  | "custom";

export type LocalReminderDay =
  | "mon"
  | "tue"
  | "wed"
  | "thu"
  | "fri"
  | "sat"
  | "sun";

export type LocalReminderSchedule =
  | {
      type: "daily";
      times?: string[];
    }
  | {
      type: "weekly";
      times?: string[];
      days?: LocalReminderDay[];
    }
  | {
      type: "interval";
      every_minutes?: number;
    };

export type LocalReminder = {
  id: string;
  category: LocalReminderCategory;
  title_key: string;
  body_key: string;
  body_params?: Record<string, string>;
  schedule: LocalReminderSchedule;
  enabled: boolean;
  deep_link?: string;
  last_fired_at?: string;
  next_fire_at?: string;
};

export type QuietHours = {
  start: string;
  end: string;
};

export type LocalReminderPermission = "granted" | "denied" | "prompt" | "unknown";

type PreferencesLike = {
  get(input: { key: string }): Promise<{ value: string | null }>;
  set(input: { key: string; value: string }): Promise<void>;
  remove(input: { key: string }): Promise<void>;
};

type LocalNotificationsLike = {
  checkPermissions?: () => Promise<{ display?: string; receive?: string }>;
  requestPermissions?: () => Promise<{ display?: string; receive?: string }>;
  cancel?: (input: { notifications: Array<{ id: number }> }) => Promise<void>;
  schedule?: (input: { notifications: ScheduledLocalNotification[] }) => Promise<unknown>;
  addListener?: (
    eventName: "localNotificationActionPerformed",
    listener: (event: LocalNotificationActionPerformed) => void | Promise<void>,
  ) => Promise<{ remove: () => void }> | { remove: () => void };
};

type RouterLike = {
  history: {
    push(path: string): void;
  };
};

type LocalNotificationActionPerformed = {
  notification?: {
    extra?: Record<string, unknown>;
  };
};

export type ScheduledLocalNotification = {
  id: number;
  title: string;
  body: string;
  schedule: { at: Date };
  extra: {
    deep_link?: string;
    reminder_id: string;
    title_key: string;
    body_key: string;
    body_params?: Record<string, string>;
  };
};

export interface RescheduleOptions {
  now?: Date;
  reminders?: LocalReminder[];
  quietHours?: QuietHours | null;
  preferences?: PreferencesLike;
  localNotifications?: LocalNotificationsLike;
  onSkipQuietHours?: (payload: { reminderId: string; at: Date }) => void;
}

export const LOCAL_REMINDERS_KEY = "pssmngr.localReminders.v1";
export const LOCAL_REMINDERS_SEEDED_KEY = "pssmngr.localReminders.seeded.v1";
export const LOCAL_REMINDER_QUIET_HOURS_KEY = "pssmngr.localReminders.quietHours.v1";
export const LOCAL_REMINDER_OCCURRENCES = 30;

const DEFAULT_DEEP_LINK = "/app/vault/health";
const logger = getLogger();

const DAYS: LocalReminderDay[] = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const DAY_TO_INDEX = new Map<LocalReminderDay, number>(
  DAYS.map((day, index) => [day, index]),
);

const DEFAULT_REMINDERS: Array<Omit<LocalReminder, "id">> = [
  {
    category: "rotation",
    title_key: "reminders.copy.rotation.title",
    body_key: "reminders.copy.rotation.body",
    schedule: { type: "weekly", days: ["mon"], times: ["09:00"] },
    enabled: false,
    deep_link: DEFAULT_DEEP_LINK,
  },
  {
    category: "review_weak",
    title_key: "reminders.copy.review_weak.title",
    body_key: "reminders.copy.review_weak.body",
    schedule: { type: "weekly", days: ["sun"], times: ["18:00"] },
    enabled: false,
    deep_link: DEFAULT_DEEP_LINK,
  },
  {
    category: "review_breached",
    title_key: "reminders.copy.review_breached.title",
    body_key: "reminders.copy.review_breached.body",
    schedule: { type: "weekly", days: ["wed"], times: ["12:00"] },
    enabled: false,
    deep_link: DEFAULT_DEEP_LINK,
  },
  {
    category: "backup_unverified",
    title_key: "reminders.copy.backup_unverified.title",
    body_key: "reminders.copy.backup_unverified.body",
    schedule: { type: "daily", times: ["20:00"] },
    enabled: false,
    deep_link: "/app/settings/import-export",
  },
];

function createReminderId(): string {
  const cryptoWithRandom = globalThis.crypto as Crypto | undefined;
  if (cryptoWithRandom?.randomUUID) return cryptoWithRandom.randomUUID();
  return `reminder-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getPreferences(preferences?: PreferencesLike): PreferencesLike {
  return preferences ?? (native.preferences as unknown as PreferencesLike);
}

function getLocalNotifications(
  localNotifications?: LocalNotificationsLike,
): LocalNotificationsLike {
  return localNotifications ?? (native.localNotifications as unknown as LocalNotificationsLike);
}

function safeParseReminders(value: string | null): LocalReminder[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isLocalReminder);
  } catch {
    return [];
  }
}

function isLocalReminder(value: unknown): value is LocalReminder {
  if (!value || typeof value !== "object") return false;
  const reminder = value as LocalReminder;
  return (
    typeof reminder.id === "string" &&
    typeof reminder.category === "string" &&
    typeof reminder.title_key === "string" &&
    typeof reminder.body_key === "string" &&
    typeof reminder.enabled === "boolean" &&
    Boolean(reminder.schedule) &&
    typeof reminder.schedule.type === "string"
  );
}

function normalizeTime(time: string): string | null {
  const match = time.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

function normalizeTimes(times: string[] | undefined): string[] {
  const normalized = (times ?? ["09:00"])
    .map((time) => normalizeTime(time))
    .filter((time): time is string => Boolean(time));
  return Array.from(new Set(normalized)).sort();
}

function withTime(base: Date, time: string): Date {
  const [hour = "0", minute = "0"] = time.split(":");
  const next = new Date(base);
  next.setHours(Number(hour), Number(minute), 0, 0);
  return next;
}

function startOfDay(date: Date): Date {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function minutesFromTime(time: string): number {
  const [hour = "0", minute = "0"] = time.split(":");
  return Number(hour) * 60 + Number(minute);
}

function isInQuietHours(date: Date, quietHours?: QuietHours | null): boolean {
  if (!quietHours) return false;
  const start = normalizeTime(quietHours.start);
  const end = normalizeTime(quietHours.end);
  if (!start || !end || start === end) return false;

  const current = date.getHours() * 60 + date.getMinutes();
  const startMinutes = minutesFromTime(start);
  const endMinutes = minutesFromTime(end);

  if (startMinutes < endMinutes) {
    return current >= startMinutes && current < endMinutes;
  }

  return current >= startMinutes || current < endMinutes;
}

export async function listLocalReminders(
  preferences?: PreferencesLike,
): Promise<LocalReminder[]> {
  const { value } = await getPreferences(preferences).get({ key: LOCAL_REMINDERS_KEY });
  return safeParseReminders(value);
}

export async function saveLocalReminders(
  reminders: LocalReminder[],
  preferences?: PreferencesLike,
): Promise<void> {
  await getPreferences(preferences).set({
    key: LOCAL_REMINDERS_KEY,
    value: JSON.stringify(reminders),
  });
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("pssmngr:local-reminders-changed"));
  }
}

export async function createLocalReminder(
  reminder: Omit<LocalReminder, "id"> & { id?: string },
  preferences?: PreferencesLike,
): Promise<LocalReminder> {
  const reminders = await listLocalReminders(preferences);
  const created: LocalReminder = {
    ...reminder,
    id: reminder.id ?? createReminderId(),
  };
  await saveLocalReminders([...reminders, created], preferences);
  return created;
}

export async function updateLocalReminder(
  id: string,
  patch: Partial<Omit<LocalReminder, "id">>,
  preferences?: PreferencesLike,
): Promise<LocalReminder> {
  const reminders = await listLocalReminders(preferences);
  let updated: LocalReminder | null = null;
  const next = reminders.map((reminder) => {
    if (reminder.id !== id) return reminder;
    updated = { ...reminder, ...patch };
    return updated;
  });

  if (!updated) throw new Error(`Local reminder not found: ${id}`);
  await saveLocalReminders(next, preferences);
  return updated;
}

export async function deleteLocalReminder(
  id: string,
  preferences?: PreferencesLike,
  localNotifications?: LocalNotificationsLike,
): Promise<void> {
  const reminders = await listLocalReminders(preferences);
  await cancelReminderNotifications(id, localNotifications);
  await saveLocalReminders(
    reminders.filter((reminder) => reminder.id !== id),
    preferences,
  );
}

export async function seedDefaultLocalReminders(
  preferences?: PreferencesLike,
): Promise<LocalReminder[]> {
  const prefs = getPreferences(preferences);
  const [{ value: seeded }, existing] = await Promise.all([
    prefs.get({ key: LOCAL_REMINDERS_SEEDED_KEY }),
    listLocalReminders(prefs),
  ]);

  if (seeded === "true" || existing.length > 0) return existing;

  const defaults = DEFAULT_REMINDERS.map((reminder) => ({
    ...reminder,
    id: createReminderId(),
  }));
  await prefs.set({ key: LOCAL_REMINDERS_SEEDED_KEY, value: "true" });
  await saveLocalReminders(defaults, prefs);
  return defaults;
}

export async function getLocalNotificationPermission(
  localNotifications?: LocalNotificationsLike,
): Promise<LocalReminderPermission> {
  if (!native.isNative) return "unknown";
  const plugin = getLocalNotifications(localNotifications);
  const result = await plugin.checkPermissions?.();
  return normalizePermission(result?.display ?? result?.receive);
}

export async function requestLocalNotificationPermission(
  localNotifications?: LocalNotificationsLike,
): Promise<LocalReminderPermission> {
  if (!native.isNative) return "unknown";
  const plugin = getLocalNotifications(localNotifications);
  const result = await plugin.requestPermissions?.();
  const permission = normalizePermission(result?.display ?? result?.receive);
  if (permission === "granted") {
    await seedDefaultLocalReminders();
  }
  return permission;
}

function normalizePermission(value: string | undefined): LocalReminderPermission {
  if (value === "granted") return "granted";
  if (value === "denied") return "denied";
  if (value === "prompt" || value === "prompt-with-rationale") return "prompt";
  return "unknown";
}

export async function openNotificationSettings(): Promise<boolean> {
  if (!native.isNative) return false;
  const appWithSettings = native.app as typeof native.app & {
    openSettings?: () => Promise<void>;
  };
  if (!appWithSettings.openSettings) return false;
  await appWithSettings.openSettings();
  return true;
}

export function deterministicNotificationId(
  reminderId: string,
  occurrenceIndex: number,
): number {
  const input = `${reminderId}:${occurrenceIndex}`;
  let hash = 2166136261;
  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % 2147483647 || 1;
}

export function expandReminderOccurrences(
  reminder: LocalReminder,
  options: {
    now?: Date;
    limit?: number;
    quietHours?: QuietHours | null;
    onSkipQuietHours?: (payload: { reminderId: string; at: Date }) => void;
  } = {},
): Date[] {
  const now = options.now ?? new Date();
  const limit = options.limit ?? LOCAL_REMINDER_OCCURRENCES;
  const occurrences: Date[] = [];
  const quietHours = options.quietHours;

  const pushIfAllowed = (candidate: Date) => {
    if (candidate <= now) return;
    if (isInQuietHours(candidate, quietHours)) {
      options.onSkipQuietHours?.({ reminderId: reminder.id, at: candidate });
      return;
    }
    occurrences.push(candidate);
  };

  if (reminder.schedule.type === "interval") {
    const everyMinutes = Math.max(1, reminder.schedule.every_minutes ?? 60);
    let cursor = new Date(now.getTime() + everyMinutes * 60_000);
    let guard = 0;
    while (occurrences.length < limit && guard < limit * 4) {
      pushIfAllowed(cursor);
      cursor = new Date(cursor.getTime() + everyMinutes * 60_000);
      guard += 1;
    }
    return occurrences;
  }

  const times = normalizeTimes(reminder.schedule.times);
  let cursorDay = startOfDay(now);
  let guard = 0;

  while (occurrences.length < limit && guard < 370) {
    const day = DAYS[cursorDay.getDay()]!;
    const weeklyDays =
      reminder.schedule.type === "weekly"
        ? reminder.schedule.days?.length
          ? reminder.schedule.days
          : ["mon"]
        : null;
    const dayAllowed = reminder.schedule.type === "daily" || weeklyDays?.includes(day);

    if (dayAllowed) {
      for (const time of times) {
        pushIfAllowed(withTime(cursorDay, time));
        if (occurrences.length >= limit) break;
      }
    }

    cursorDay = addDays(cursorDay, 1);
    guard += 1;
  }

  return occurrences.sort((a, b) => a.getTime() - b.getTime());
}

export async function cancelReminderNotifications(
  reminderId: string,
  localNotifications?: LocalNotificationsLike,
): Promise<void> {
  if (!native.isNative) return;
  const plugin = getLocalNotifications(localNotifications);
  await plugin.cancel?.({
    notifications: Array.from({ length: LOCAL_REMINDER_OCCURRENCES }, (_, index) => ({
      id: deterministicNotificationId(reminderId, index),
    })),
  });
}

export async function rescheduleLocalReminders(
  options: RescheduleOptions = {},
): Promise<LocalReminder[]> {
  if (!native.isNative) return options.reminders ?? [];

  const preferences = getPreferences(options.preferences);
  const localNotifications = getLocalNotifications(options.localNotifications);
  const reminders = options.reminders ?? (await listLocalReminders(preferences));
  const quietHours =
    options.quietHours === undefined
      ? await getCachedQuietHours(preferences)
      : options.quietHours;
  const now = options.now ?? new Date();
  const nextReminders: LocalReminder[] = [];

  for (const reminder of reminders) {
    await cancelReminderNotifications(reminder.id, localNotifications);
    if (!reminder.enabled) {
      const withoutNextFireAt = { ...reminder };
      delete withoutNextFireAt.next_fire_at;
      nextReminders.push(withoutNextFireAt);
      continue;
    }

    const occurrences = expandReminderOccurrences(reminder, {
      now,
      quietHours,
      onSkipQuietHours: (payload) => {
        logger.debug("Skipped local reminder during quiet hours", {
          function: "rescheduleLocalReminders",
          reminderId: payload.reminderId,
          at: payload.at.toISOString(),
        });
        options.onSkipQuietHours?.(payload);
      },
    });

    const notifications = occurrences.map((at, index): ScheduledLocalNotification => {
      const extra: ScheduledLocalNotification["extra"] = {
        reminder_id: reminder.id,
        title_key: reminder.title_key,
        body_key: reminder.body_key,
      };
      if (reminder.deep_link) extra.deep_link = reminder.deep_link;
      if (reminder.body_params) extra.body_params = reminder.body_params;

      return {
        id: deterministicNotificationId(reminder.id, index),
        title: String(
          i18n.t(reminder.title_key, {
            ns: "reminders",
            ...(reminder.body_params ?? {}),
          }),
        ),
        body: String(
          i18n.t(reminder.body_key, {
            ns: "reminders",
            ...(reminder.body_params ?? {}),
          }),
        ),
        schedule: { at },
        extra,
      };
    });

    if (notifications.length > 0) {
      await localNotifications.schedule?.({ notifications });
    }

    const nextReminder = { ...reminder };
    if (occurrences[0]) {
      nextReminder.next_fire_at = occurrences[0].toISOString();
    } else {
      delete nextReminder.next_fire_at;
    }
    nextReminders.push(nextReminder);
  }

  await preferences.set({
    key: LOCAL_REMINDERS_KEY,
    value: JSON.stringify(nextReminders),
  });
  return nextReminders;
}

export async function markReminderFired(
  reminderId: string,
  firedAt = new Date(),
  preferences?: PreferencesLike,
): Promise<void> {
  const reminders = await listLocalReminders(preferences);
  await saveLocalReminders(
    reminders.map((reminder) =>
      reminder.id === reminderId
        ? { ...reminder, last_fired_at: firedAt.toISOString() }
        : reminder,
    ),
    preferences,
  );
}

export async function refreshQuietHoursFromPushPreferences(
  preferences?: PreferencesLike,
): Promise<QuietHours | null> {
  try {
    const summaries = await trpcQuery<
      Array<{ quietHoursStart: string | null; quietHoursEnd: string | null }>
    >("notification", "getPreferences");

    const first = summaries.find(
      (summary) => summary.quietHoursStart && summary.quietHoursEnd,
    );
    const quietHours =
      first?.quietHoursStart && first.quietHoursEnd
        ? { start: first.quietHoursStart, end: first.quietHoursEnd }
        : null;
    await setCachedQuietHours(quietHours, preferences);
    return quietHours;
  } catch (error) {
    logger.debug("Could not refresh push quiet hours for local reminders", {
      function: "refreshQuietHoursFromPushPreferences",
      message: error instanceof Error ? error.message : "Unknown error",
    });
    return getCachedQuietHours(preferences);
  }
}

export async function getCachedQuietHours(
  preferences?: PreferencesLike,
): Promise<QuietHours | null> {
  const { value } = await getPreferences(preferences).get({
    key: LOCAL_REMINDER_QUIET_HOURS_KEY,
  });
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as QuietHours;
    if (normalizeTime(parsed.start) && normalizeTime(parsed.end)) return parsed;
  } catch {
    return null;
  }
  return null;
}

export async function setCachedQuietHours(
  quietHours: QuietHours | null,
  preferences?: PreferencesLike,
): Promise<void> {
  const prefs = getPreferences(preferences);
  if (!quietHours) {
    await prefs.remove({ key: LOCAL_REMINDER_QUIET_HOURS_KEY });
    return;
  }
  await prefs.set({
    key: LOCAL_REMINDER_QUIET_HOURS_KEY,
    value: JSON.stringify(quietHours),
  });
}

function normalizeDeepLink(deepLink: string): string {
  const toDashboardPath = (path: string) => {
    const clean = path.replace(/^\/+/, "");
    if (clean === "vault/health") return "/dashboard/watchtower";
    return clean.startsWith("dashboard") ? `/${clean}` : `/dashboard/${clean}`;
  };

  if (deepLink.startsWith("https://pssmngr.com/app/")) {
    return toDashboardPath(deepLink.slice("https://pssmngr.com/app/".length));
  }
  if (deepLink.startsWith("/app/")) {
    return toDashboardPath(deepLink.slice("/app/".length));
  }
  return deepLink.startsWith("/") ? deepLink : `/${deepLink}`;
}

export async function routeLocalReminderTap(
  event: LocalNotificationActionPerformed,
  router: RouterLike,
): Promise<void> {
  const extra = event.notification?.extra ?? {};
  const reminderId = typeof extra.reminder_id === "string" ? extra.reminder_id : null;
  const deepLink = typeof extra.deep_link === "string" ? extra.deep_link : null;

  if (reminderId) {
    await markReminderFired(reminderId);
  }

  if (!deepLink) return;

  const routedPath = normalizeDeepLink(deepLink);
  const requiresVaultUnlock =
    routedPath.startsWith("/dashboard/vault") ||
    routedPath.startsWith("/dashboard/watchtower");
  if (requiresVaultUnlock && !vaultKeyGate.isUnlocked()) {
    try {
      await vaultKeyGate.requireUnlocked("Open your vault reminder");
    } catch {
      router.history.push("/login");
      return;
    }
  }

  router.history.push(routedPath);
}

export function mountLocalReminderScheduler(router: RouterLike): () => void {
  if (!native.isNative) return () => {};

  const handles: Array<Promise<{ remove: () => void }> | { remove: () => void }> = [];
  const rerun = () => {
    void refreshQuietHoursFromPushPreferences().then((quietHours) =>
      rescheduleLocalReminders({ quietHours }),
    );
  };

  rerun();
  window.addEventListener("pssmngr:local-reminders-changed", rerun);
  i18n.on("languageChanged", rerun);

  handles.push(
    native.localNotifications.addListener?.("localNotificationActionPerformed", (event) => {
      void routeLocalReminderTap(event, router);
    }) ?? { remove: () => {} },
  );

  return () => {
    window.removeEventListener("pssmngr:local-reminders-changed", rerun);
    i18n.off("languageChanged", rerun);
    for (const handle of handles) {
      void Promise.resolve(handle).then((resolved) => resolved.remove());
    }
  };
}

export const __testing = {
  DAY_TO_INDEX,
  isInQuietHours,
  normalizeDeepLink,
  normalizeTime,
};
